import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom/dist";
import Table from "../../../../core/pagination/datatable";
import CommonFooter from "../../../../core/common/footer/commonFooter";
import { RotateCcw, Tag } from "feather-icons-react/build/IconComponents";
import { Select, Spin } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import ImageWithBasePath from "../../../../core/img/imagewithbasebath";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import AddSwipeLocationName from "./AddSwipeLocationName";
import {
  ROLL_CALL_API_BASE_URL,
  ROLL_CALL_EMPLOYEES_LIST,
} from "../RollCallConfig/RollCallConfig";

const SwipeLocationNames = () => {
  const today = new Date().toISOString().split("T")[0];
  const navigate = useNavigate();
  const location = useLocation();
  const [tokenData, setTokenData] = useState(null);
  const [superIdData, setSuperIdData] = useState(null);
  const [perm, setPerm] = useState({
    Read: false,
    Write: false,
    Delete: false,
    Export: false,
  });
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [selectedStartDate, setSelectedStartDate] = useState(today);
  const [selectedEndDate, setSelectedEndDate] = useState(today);
  const [swipeData, setSwipeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nicknameMap, setNicknameMap] = useState({});
  const [showCanvas, setShowCanvas] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  useEffect(() => {
    const stored = sessionStorage.getItem("rollcall-permissions");
    if (!stored) return;
    const map = JSON.parse(stored);
    const path = location.pathname.slice(1);
    const perms = map[path];
    if (!perms) {
      navigate("/rollcall/dashboard");
      return;
    }
    setPerm({
      Read: perms.canRead,
      Write: perms.canWrite,
      Delete: perms.canDelete,
      Export: perms.canExport,
    });
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    const superId = sessionStorage.getItem("superId");
    if (token && superId) {
      setTokenData(token);
      setSuperIdData(superId);
    } else {
      navigate("/signin");
    }
  }, [navigate]);

  useEffect(() => {
    if (!tokenData || !superIdData) return;
    fetch(
      `${ROLL_CALL_API_BASE_URL}${ROLL_CALL_EMPLOYEES_LIST}?superid=${superIdData}`,
      { method: "GET", headers: { Authorization: `Bearer ${tokenData}` } },
    )
      .then((res) => res.json())
      .then((data) => {
        if (data?.status) setEmployees(data.ResultData || []);
      })
      .catch(console.error);
  }, [tokenData, superIdData]);

  const fetchLocationNames = () => {
    if (!tokenData || !superIdData) return;
    fetch(
      `${ROLL_CALL_API_BASE_URL}portal/reports/getswipelocationname?superid=${superIdData}&RegId=${selectedEmp || 0}`,
      { method: "GET", headers: { Authorization: `Bearer ${tokenData}` } },
    )
      .then((res) => res.json())
      .then((data) => {
        if (data?.status) {
          const map = {};
          (data.ResultData || []).forEach((item) => {
            map[String(item.refId)] = item.Name;
          });
          setNicknameMap(map);
        }
      })
      .catch(console.error);
  };

  useEffect(() => {
    if (tokenData && superIdData) fetchLocationNames();
  }, [tokenData, superIdData]);

  const fetchSwipeData = async () => {
    if (!tokenData || !superIdData) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("superid", superIdData);
      params.append("RegId", selectedEmp || 0);
      params.append("Fromdate", selectedStartDate);
      params.append("Todate", selectedEndDate);

      const response = await fetch(
        `${ROLL_CALL_API_BASE_URL}portal/reports/getswipelocations?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tokenData}`,
          },
        },
      );

      if (!response.ok) throw new Error("Failed to fetch swipe data");

      const data = await response.json();
      setSwipeData(data.status ? data.ResultData || [] : []);
    } catch (error) {
      console.error("Error fetching swipe data:", error);
      setSwipeData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tokenData && superIdData) fetchSwipeData();
  }, [tokenData, superIdData]);

  const handleSubmit = () => fetchSwipeData();

  const handleReload = () => {
    setSelectedEmp(null);
    setSelectedStartDate(today);
    setSelectedEndDate(today);
    fetchSwipeData();
    fetchLocationNames();
  };

  const getNickname = (swipeLocationId) =>
    nicknameMap[String(swipeLocationId)] || null;

  const openAddNameCanvas = (record) => {
    setSelectedRecord(record);
    setShowCanvas(true);
  };

  const handleSaveName = (name, record) => {
    setNicknameMap((prev) => ({
      ...prev,
      [String(record.SwipeLocationId)]: name,
    }));
  };

  const exportRows = () =>
    swipeData.map((item, index) => ({
      "S.No": index + 1,
      Employee: item.Employee,
      "Biometric ID": item["Biometric Id"],
      "Date & Time": item.DateOftransaction,
      "Swipe Location": item["Swipe Location"],
      "Location Name": getNickname(item.SwipeLocationId) || "—",
      Latitude: item.lat,
      Longitude: item.lon,
    }));

  const handleDownloadExcel = () => {
    if (!swipeData.length) return;
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportRows());
    XLSX.utils.book_append_sheet(wb, ws, "Swipe Details");
    XLSX.writeFile(wb, "Swipe_Details.xlsx");
  };

  const generatePDF = () => {
    if (!swipeData.length) return;
    const doc = new jsPDF("p", "mm", "a4");
    doc.text("Swipe Details Report", 14, 10);
    doc.autoTable({
      head: [
        ["S.No", "Employee", "Biometric ID", "Date & Time", "Location", "Name"],
      ],
      body: swipeData.map((item, i) => [
        i + 1,
        item.Employee,
        item["Biometric Id"],
        item.DateOftransaction,
        item["Swipe Location"],
        getNickname(item.SwipeLocationId) || "—",
      ]),
    });
    doc.save("Swipe_Details.pdf");
  };

  const renderTooltip = (props) => (
    <Tooltip id="pdf-tooltip" {...props}>
      PDF
    </Tooltip>
  );
  const renderExcelTooltip = (props) => (
    <Tooltip id="excel-tooltip" {...props}>
      Excel
    </Tooltip>
  );
  const renderRefreshTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Refresh
    </Tooltip>
  );

  const columns = [
    {
      title: "Employee",
      dataIndex: "Employee",
      sorter: (a, b) => a.Employee?.localeCompare(b.Employee),
      render: (text) => (
        <span>{text?.length > 15 ? `${text.substring(0, 15)}...` : text}</span>
      ),
    },
    {
      title: "Biometric",
      dataIndex: "Biometric Id",
      sorter: (a, b) =>
        (a["Biometric Id"] || "").localeCompare(b["Biometric Id"] || ""),
      render: (_, record) => record["Biometric Id"],
    },
    {
      title: "Date & Time",
      dataIndex: "DateOftransaction",
      sorter: (a, b) =>
        (a.DateOftransaction || "").localeCompare(b.DateOftransaction || ""),
      render: (text) => {
        if (!text) return "N/A";

        const date = new Date(text);

        if (isNaN(date.getTime())) {
          return text; // fallback if parsing fails
        }

        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");

        return `${day}/${month}/${year} ${hours}:${minutes}`;
      },
    },
    {
      title: "Swipe Location",
      dataIndex: "Swipe Location",
      sorter: (a, b) =>
        (a["Swipe Location"] || "").localeCompare(b["Swipe Location"] || ""),
      render: (_, record) => {
        const location = record["Swipe Location"] || "";
        const displayText =
          location.length > 30 ? location.substring(0, 30) + "..." : location;

        return (
          <span
            title={location} // Full text on hover
          >
            {displayText}
          </span>
        );
      },
    },

    {
      title: "Action",
      dataIndex: "action",
      render: (_, record) => {
        const nick = getNickname(record.SwipeLocationId);
        return (
          <div className="d-flex align-items-center gap-3">
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>View on Map</Tooltip>}
            >
              <a
                href={`https://www.google.com/maps?q=${record.lat},${record.lon}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <i
                  className="ti ti-map-pin"
                  style={{ color: "rgb(254, 159, 67)", fontSize: "18px" }}
                />
              </a>
            </OverlayTrigger>

            {perm?.Write && (
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>{nick ? "Edit Name" : "Add Name"}</Tooltip>}
              >
                <Link
                  to=""
                  className="dropdown-item p-0"
                  onClick={(e) => {
                    e.preventDefault();
                    openAddNameCanvas(record);
                  }}
                >
                  <Tag
                    size={16}
                    className={nick ? "text-warning" : "text-info"}
                  />
                </Link>
              </OverlayTrigger>
            )}
          </div>
        );
      },
    },
  ];

  const empOptions = employees.map((emp) => ({
    value: emp.RegId,
    label: emp.UserName,
  }));

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/rollcall/dashboard">
                        <i className="fas fa-home fa-lg" />
                      </Link>
                    </li>
                    <h4 className="breadcrumb-item" aria-current="page">
                      Swipe Details
                    </h4>
                  </ol>
                </nav>
                <h6>View swipe records &amp; assign location names</h6>
              </div>
            </div>

            <ul className="table-top-head">
              {perm?.Export && (
                <>
                  <li>
                    <OverlayTrigger placement="top" overlay={renderTooltip}>
                      <Link onClick={generatePDF}>
                        <ImageWithBasePath
                          src="assets/img/icons/pdf.svg"
                          alt="img"
                        />
                      </Link>
                    </OverlayTrigger>
                  </li>
                  <li>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderExcelTooltip}
                    >
                      <Link onClick={handleDownloadExcel}>
                        <ImageWithBasePath
                          src="assets/img/icons/excel.svg"
                          alt="img"
                        />
                      </Link>
                    </OverlayTrigger>
                  </li>
                </>
              )}
              <li onClick={handleReload}>
                <OverlayTrigger placement="top" overlay={renderRefreshTooltip}>
                  <Link>
                    <RotateCcw />
                  </Link>
                </OverlayTrigger>
              </li>
            </ul>
          </div>

          {perm.Read ? (
            <div className="card table-list-card">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                <div className="search-set" />
                <div className="d-flex justify-content-end align-items-end row">
                  <div className="col-4">
                    <Select
                      showSearch
                      placeholder="Choose Employee"
                      value={selectedEmp}
                      onChange={setSelectedEmp}
                      allowClear
                      options={empOptions}
                      style={{ width: 190, height: 37 }}
                      filterOption={(input, option) =>
                        option.label.toLowerCase().includes(input.toLowerCase())
                      }
                    />
                  </div>
                  <div className="col-3">
                    <input
                      type="date"
                      className="form-control"
                      value={selectedStartDate}
                      onChange={(e) => setSelectedStartDate(e.target.value)}
                    />
                  </div>
                  <div className="col-3">
                    <input
                      type="date"
                      className="form-control"
                      value={selectedEndDate}
                      onChange={(e) => setSelectedEndDate(e.target.value)}
                    />
                  </div>
                  <div className="col-2">
                    <button className="btn btn-primary" onClick={handleSubmit}>
                      <FontAwesomeIcon icon={faMagnifyingGlass} />
                    </button>
                  </div>
                </div>
              </div>

              <div className="card-body">
                <div className="table-responsive brand-table">
                  {loading ? (
                    <div className="text-center my-4">
                      <Spin size="large" />
                      <p>Loading data...</p>
                    </div>
                  ) : swipeData.length > 0 ? (
                    <Table
                      columns={columns}
                      dataSource={swipeData}
                      pgno={true}
                    />
                  ) : (
                    <p className="text-center my-4">No data available</p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-body d-flex justify-content-center">
                <h1>You don&apos;t have access to this page</h1>
              </div>
            </div>
          )}
        </div>
        <CommonFooter />

        <AddSwipeLocationName
          isOpen={showCanvas}
          onClose={() => setShowCanvas(false)}
          record={selectedRecord}
          existingName={
            selectedRecord ? getNickname(selectedRecord.SwipeLocationId) : null
          }
          onSave={handleSaveName}
        />
      </div>
    </div>
  );
};

export default SwipeLocationNames;
